import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useClinic } from '../../context/ClinicContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  HiOutlineHome,
  HiOutlineUserGroup,
  HiOutlineUsers,
  HiOutlineClipboardList,
  HiOutlineCreditCard,
  HiOutlineDocumentText,
  HiOutlineX,
  HiOutlineUserCircle,
  HiOutlineCurrencyRupee,
  HiOutlineAcademicCap,
  HiOutlineChartBar,
  HiOutlineCalendar,
  HiOutlineClipboardCheck,
  HiOutlineDocumentDuplicate,
  HiOutlineCog,
  HiOutlineReceiptRefund,
  HiOutlineDocumentSearch,
  HiOutlineMail,
  HiOutlineBriefcase,
  HiOutlineOfficeBuilding,
  HiOutlineChatAlt2,
  HiOutlinePencilAlt,
  HiOutlineLockClosed,
  HiChevronDown,
  HiChevronRight
} from 'react-icons/hi';
import krishnaLogo from '../../assets/krishnalogo.png';
import { userAvatarSrc } from '../../utils/avatarUrl';
import { staffApi } from '../../services/api';

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { monthlyReviewsTherapistEnabled, monthlyReviewsVisibilityLoaded } = useClinic();
  const { canRead, isTherapist } = usePermissions();
  const location = useLocation();
  const [configOpen, setConfigOpen] = useState(false);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const sidebarAvatar = userAvatarSrc(user);
  const prevPathRef = useRef(location.pathname);

  const refreshPendingLeaveCount = useCallback(async () => {
    if (!user || !canRead('staff')) {
      setPendingLeaveCount(0);
      return;
    }
    try {
      const res = await staffApi.getLeaveRequestsPendingCount();
      setPendingLeaveCount(res.data.data?.pending_count ?? 0);
    } catch {
      setPendingLeaveCount(0);
    }
  }, [user, canRead]);

  useEffect(() => {
    refreshPendingLeaveCount();
    const t = setInterval(refreshPendingLeaveCount, 60000);
    const onStaffLeaveEvent = () => refreshPendingLeaveCount();
    window.addEventListener('pendingLeaveRequestsChanged', onStaffLeaveEvent);
    return () => {
      clearInterval(t);
      window.removeEventListener('pendingLeaveRequestsChanged', onStaffLeaveEvent);
    };
  }, [refreshPendingLeaveCount]);

  // Check if any config submenu item is active
  const isConfigActive = location.pathname === '/leave-types' || 
                         location.pathname === '/diagnosis-codes' ||
                         location.pathname === '/lead-sources' ||
                         location.pathname === '/lead-statuses' ||
                         location.pathname === '/therapy-types' ||
                         location.pathname === '/plan-types' ||
                         location.pathname === '/invoice-items' ||
                         location.pathname === '/email-settings' ||
                         location.pathname === '/whatsapp-settings' ||
                         location.pathname === '/attendance-settings' ||
                         location.pathname === '/staff-leave-opening' ||
                         location.pathname === '/job-position-types' ||
                         location.pathname === '/templates' ||
                         location.pathname === '/clinics' ||
                         location.pathname === '/roles' ||
                         location.pathname.startsWith('/clinics/');

  const isAdminRole = user?.role === 'admin';

  const baseConfigChildren = [
    { name: 'Roles', href: '/roles', icon: HiOutlineLockClosed },
    { name: 'Leave Types', href: '/leave-types', icon: HiOutlineCog },
    { name: 'Diagnosis Codes', href: '/diagnosis-codes', icon: HiOutlineDocumentSearch },
    { name: 'Lead Sources', href: '/lead-sources', icon: HiOutlineUserGroup },
    { name: 'Lead Statuses', href: '/lead-statuses', icon: HiOutlineClipboardList },
    { name: 'Therapy Types', href: '/therapy-types', icon: HiOutlineAcademicCap },
    { name: 'Plan Types', href: '/plan-types', icon: HiOutlineCalendar },
    { name: 'Invoice Items', href: '/invoice-items', icon: HiOutlineDocumentText },
    { name: 'Gmail', href: '/email-settings', icon: HiOutlineMail },
    { name: 'WhatsApp', href: '/whatsapp-settings', icon: HiOutlineChatAlt2 },
    { name: 'Attendance automation', href: '/attendance-settings', icon: HiOutlineClipboardCheck },
    { name: 'Staff leave opening', href: '/staff-leave-opening', icon: HiOutlineUsers },
    { name: 'Job Position Types', href: '/job-position-types', icon: HiOutlineBriefcase },
    { name: 'Templates', href: '/templates', icon: HiOutlineDocumentText },
  ];
  const configChildren = isAdminRole
    ? [{ name: 'Clinics', href: '/clinics', icon: HiOutlineOfficeBuilding }, ...baseConfigChildren]
    : baseConfigChildren;

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HiOutlineHome, module: 'dashboard' },
    { name: 'Discussions', href: '/discussions', icon: HiOutlineChatAlt2, module: 'discussions' },
    { name: 'Calendar', href: '/calendar', icon: HiOutlineCalendar, module: 'calendar' },
    { name: 'Leads', href: '/leads', icon: HiOutlineUserGroup, module: 'leads' },
    { name: 'Assessments', href: '/assessments', icon: HiOutlineClipboardList, module: 'assessments' },
    { name: 'Patients', href: '/patients', icon: HiOutlineUsers, module: 'patients' },
    { name: 'Patient Attendance', href: '/patient-attendance', icon: HiOutlineClipboardCheck, module: 'attendance' },
    { name: 'Invoices', href: '/invoices', icon: HiOutlineDocumentText, module: 'invoices' },
    { name: 'Payments', href: '/payments', icon: HiOutlineCreditCard, module: 'payments' },
    { name: 'Progress Reports', href: '/progress', icon: HiOutlineChartBar, module: 'progress' },
    { name: 'Monthly Reviews', href: '/monthly-reviews', icon: HiOutlinePencilAlt, module: 'monthly_reviews' },
    { name: 'Events', href: '/events', icon: HiOutlineAcademicCap, module: 'events' },
    { name: 'Staff', href: '/staff', icon: HiOutlineUserCircle, module: 'staff' },
    { name: 'Patient Protocols', href: '/patient-protocols', icon: HiOutlineDocumentDuplicate, module: 'protocols' },
    { name: 'Finance', href: '/finance', icon: HiOutlineCurrencyRupee, module: 'finance' },
    { name: 'Expenses', href: '/expenses', icon: HiOutlineReceiptRefund, module: 'expenses' },
    {
      name: 'Configuration',
      icon: HiOutlineCog,
      roles: ['admin', 'clinic_admin'],
      children: configChildren,
    },
  ];

  const filteredNavigation = navigation.filter((item) => {
    if (item.roles && !item.roles.includes(user?.role)) return false;
    if (item.children) return item.children.length > 0;
    if (!item.module) return true;
    if (
      item.module === 'monthly_reviews' &&
      isTherapist &&
      (!monthlyReviewsVisibilityLoaded || !monthlyReviewsTherapistEnabled)
    ) {
      return false;
    }
    return canRead(item.module);
  });

  /** Flat links for therapist mobile bottom bar (no Configuration tree on therapist). */
  const therapistMobileTabs = useMemo(
    () => filteredNavigation.filter((item) => !item.children && item.href),
    [filteredNavigation]
  );

  const therapistMobileShortLabel = (name) => {
    if (name === 'Patient Attendance') return 'Attendance';
    if (name === 'Patient Protocols') return 'Protocols';
    if (name === 'Monthly Reviews') return 'Reviews';
    if (name === 'Discussions') return 'Chats';
    if (name === 'Dashboard') return 'Home';
    return name;
  };

  // Auto-open config dropdown if on a config page
  React.useEffect(() => {
    if (isConfigActive) {
      setConfigOpen(true);
    }
  }, [isConfigActive]);

  useEffect(() => {
    if (!isOpen || isTherapist) {
      prevPathRef.current = location.pathname;
      return undefined;
    }
    if (prevPathRef.current !== location.pathname) {
      onClose();
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, isOpen, isTherapist, onClose]);

  useEffect(() => {
    if (!isOpen || isTherapist) return undefined;
    const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile()) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isTherapist]);

  const renderUserSummary = () => (
    <div className="border-t border-gray-200 shrink-0 p-2.5">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden shrink-0">
          {sidebarAvatar ? (
            <img src={sidebarAvatar} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-primary-700 font-semibold text-sm">
              {user?.full_name?.charAt(0) || 'U'}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-medium text-gray-900 truncate">
            {user?.full_name}
          </p>
          <p className="text-sm text-gray-500 capitalize">{user?.role}</p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-56 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white border-r border-gray-200">
          {/* Logo */}
          <div className="flex h-16 items-center justify-center border-b border-gray-200 px-2 py-1.5">
            <img
              src={krishnaLogo}
              alt="Krishna Pediatric Rehabilitation Clinic"
              className="h-14 w-auto max-w-full object-contain"
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 space-y-0.5 overflow-y-auto overscroll-contain px-2 py-2">
            {filteredNavigation.map((item) => {
              if (item.children) {
                return (
                  <div key={item.name}>
                    <button
                      type="button"
                      onClick={() => setConfigOpen(!configOpen)}
                      className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-base font-normal transition-colors ${
                        isConfigActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <item.icon className="w-5 h-5 shrink-0" />
                        <span className="truncate">{item.name}</span>
                      </div>
                      {configOpen ? (
                        <HiChevronDown className="w-4 h-4 shrink-0" />
                      ) : (
                        <HiChevronRight className="w-4 h-4 shrink-0" />
                      )}
                    </button>
                    {configOpen && (
                      <div className="ml-2 mt-0.5 space-y-0.5 border-l border-gray-100 pl-2">
                        {item.children.map((child) => (
                          <NavLink
                            key={child.name}
                            to={child.href}
                            className={({ isActive }) =>
                              `flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[15px] font-medium transition-colors ${
                                isActive
                                  ? 'bg-primary-100 text-primary-700'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`
                            }
                          >
                            <child.icon className="w-4 h-4 shrink-0" />
                            <span className="truncate">{child.name}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `sidebar-link w-full ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`
                  }
                >
                  <span className="flex items-center gap-2.5 min-w-0 flex-1">
                    <item.icon className="w-5 h-5 shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </span>
                  {item.href === '/staff' && pendingLeaveCount > 0 ? (
                    <span
                      className="shrink-0 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-xs font-bold leading-none"
                      title={`${pendingLeaveCount} pending leave request(s)`}
                    >
                      {pendingLeaveCount > 99 ? '99+' : pendingLeaveCount}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </nav>

          {renderUserSummary()}
        </div>
      </div>

      {/* Mobile: therapist = fixed bottom tab bar (app-style); others = slide-out drawer */}
      {isTherapist ? (
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-50 overflow-x-hidden border-t border-gray-200 bg-white/95 shadow-[0_-6px_24px_rgba(15,23,42,0.08)] backdrop-blur-md supports-[backdrop-filter]:bg-white/80"
          style={{ paddingBottom: 'max(0.35rem, env(safe-area-inset-bottom, 0px))' }}
          aria-label="Main navigation"
        >
          <div className="mx-auto flex max-w-lg items-stretch justify-around gap-0.5 overflow-x-hidden px-1 pt-1">
            {therapistMobileTabs.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href === '/dashboard'}
                className={({ isActive }) =>
                  `flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl py-2 px-1 text-center transition-colors ${
                    isActive ? 'text-primary-600' : 'text-gray-500 active:bg-gray-50'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-2xl transition-colors ${
                        isActive ? 'bg-primary-50 text-primary-600' : 'text-gray-500'
                      }`}
                    >
                      <item.icon className="h-6 w-6" aria-hidden />
                    </span>
                    <span className="max-w-full truncate text-[11px] font-semibold leading-tight">
                      {therapistMobileShortLabel(item.name)}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      ) : (
        <div
          className={`fixed inset-y-0 left-0 z-50 flex h-dvh w-[86vw] max-w-72 flex-col overflow-hidden bg-white shadow-xl transition-transform duration-300 ease-in-out lg:hidden ${
            isOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
          }`}
        >
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-3">
              <div className="flex items-center gap-2 min-w-0">
                <img
                  src={krishnaLogo}
                  alt="Krishna Pediatric Rehabilitation Clinic"
                  className="h-12 w-auto object-contain shrink-0"
                />
                <h1 className="font-display font-bold text-gray-900 text-base truncate">KRISHNA</h1>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0"
              >
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 min-h-0 space-y-0.5 overflow-y-auto overscroll-contain px-2 py-2">
              {filteredNavigation.map((item) => {
                if (item.children) {
                  return (
                    <div key={item.name}>
                      <button
                        type="button"
                        onClick={() => setConfigOpen(!configOpen)}
                        className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-base font-medium transition-colors ${
                          isConfigActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <item.icon className="w-5 h-5 shrink-0" />
                          <span className="truncate">{item.name}</span>
                        </div>
                        {configOpen ? (
                          <HiChevronDown className="w-4 h-4 shrink-0" />
                        ) : (
                          <HiChevronRight className="w-4 h-4 shrink-0" />
                        )}
                      </button>
                      {configOpen && (
                        <div className="ml-2 mt-0.5 space-y-0.5 border-l border-gray-100 pl-2">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.name}
                              to={child.href}
                              onClick={onClose}
                              className={({ isActive }) =>
                                `flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[15px] font-medium transition-colors ${
                                  isActive
                                    ? 'bg-primary-100 text-primary-700'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }`
                              }
                            >
                              <child.icon className="w-4 h-4 shrink-0" />
                              <span className="truncate">{child.name}</span>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `sidebar-link w-full ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`
                    }
                  >
                    <span className="flex items-center gap-2.5 min-w-0 flex-1">
                      <item.icon className="w-5 h-5 shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </span>
                    {item.href === '/staff' && pendingLeaveCount > 0 ? (
                      <span
                        className="shrink-0 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-xs font-bold leading-none"
                        title={`${pendingLeaveCount} pending leave request(s)`}
                      >
                        {pendingLeaveCount > 99 ? '99+' : pendingLeaveCount}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </nav>
          {renderUserSummary()}
        </div>
      )}
    </>
  );
};

export default Sidebar;

